/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */

/* CUSTOM IMPORTS */
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";

@Component({
  tag: "gx-ide-bpm-import-gxpm",
  styleUrl: "bpm-import-gxpm.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-import-gxpm"]
})
export class GxIdeTemplate {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmImportGxpmElement;

  private fileInput: HTMLInputElement;
  private automaticBackupEl: HTMLChCheckboxElement;

  // 3.STATE() VARIABLES //

  /**
   * Contains the list of files
   */
  @State() selectedFile: File = null;

  /**
   * Contains the path input value
   */
  @State() pathValue: string = "";

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;
  /**
   * Callback function to be executed when OK button is pressed.
   * This callback is expected to handle the file or string input and return a Promise that resolves when is completed.
   *
   * @param {ImportData} data - The data to be processed. This can be a string or a File object.
   * @returns {Promise<void>} A Promise that resolves to void, indicating the completion of the operation.
   */
  @Prop() readonly confirmCallback: (data: ImportData) => Promise<void>;
  /**
   * Callback invoked when user press 'Cancel' button.
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private loadFileHandle = async () => {
    this.fileInput.click();
  };

  private handleFileInputChange = (event: Event) => {
    const fileInput = event.target as HTMLInputElement;

    if (fileInput.files && fileInput.files.length > 0) {
      const newFile: File = fileInput.files[0];
      this.selectedFile = newFile;
      this.pathValue = this.selectedFile.name;
    }

    this.fileInput.value = null;
  };

  private handlePathInputChange = (event: Event) => {
    this.selectedFile = null;
    const inputElement = event.target as HTMLInputElement;
    const newValue = inputElement.value;

    this.pathValue = newValue;
  };

  private confirmCallbackHandle = async () => {
    if (this.confirmCallback) {
      const fileName = this.pathValue
        .replace(/^.*?([^\\\/]*)$/, "$1")
        .toLowerCase()
        .trim();
      if (fileName === ".gxpm" || !fileName.endsWith(".gxpm")) {
        return;
      }
      const fileData = this.selectedFile
        ? this.selectedFile
        : new File([this.pathValue], fileName);

      const data: ImportData = {
        automaticBackup: this.automaticBackupEl.value === "true",
        file: fileData
      };

      await this.confirmCallback(data);

      this.fileInput.value = null;
      this.selectedFile = null;
      this.pathValue = "";
      this.automaticBackupEl.value = "false";
    }
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <div class="header">
              <label htmlFor="project-path">
                {this._componentLocale.main.projectPathInputLabel}
              </label>
              <input
                type="text"
                class="input-text"
                id="project-path"
                value={this.pathValue}
                onChange={this.handlePathInputChange}
              />
              <input
                type="file"
                id="input_file"
                accept=".gxpm"
                ref={el => (this.fileInput = el as HTMLInputElement)}
                onChange={this.handleFileInputChange}
                class="hidden-input"
              />
              <button onClick={this.loadFileHandle}>
                {this._componentLocale.main.addFilesButton}
              </button>
            </div>

            <div class="control-wrapper">
              <label htmlFor="automatic-backup">
                {this._componentLocale.main.automaticBackupCheckboxLabel}
              </label>

              <ch-checkbox
                id="automatic-backup"
                accessibleName="automatic-backup"
                checkedValue="true"
                unCheckedValue="false"
                value="checked"
                ref={(el: HTMLChCheckboxElement) =>
                  (this.automaticBackupEl = el as HTMLChCheckboxElement)
                }
              ></ch-checkbox>
            </div>
            <button slot="footer-end" onClick={this.cancelCallback}>
              {this._componentLocale.footer.cancelButton}
            </button>
            <button slot="footer-end" onClick={this.confirmCallbackHandle}>
              {this._componentLocale.footer.confirmButton}
            </button>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type ImportData = {
  file: File;
  automaticBackup: boolean;
};
